import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import './SpintossStats.css';

const SpintossStats = () => {
  const token = localStorage.getItem('token');

  // Filters State
  const [localisation, setLocalisation] = useState("All");
  const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));

  // Data States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsV, setProductsV] = useState([]);
  const [bills, setBills] = useState([]);
  const [billsValises, setBillsValises] = useState([]);
  const [rapports, setRapports] = useState([]);

  // Helper function to extract ID robustly
  const extractLocId = (loc) => {
    if (!loc) return null;
    if (typeof loc === "string") return loc;
    if (loc.$oid) return loc.$oid;
    if (loc._id) {
      if (typeof loc._id === 'string') return loc._id;
      if (loc._id.$oid) return loc._id.$oid;
      return loc._id.toString();
    }
    return loc.toString();
  };

  // Fetch all required data
  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError("");
    try {
      const headers = { Authorization: token ? `Bearer ${token}` : "" };

      // Format start and end date to include the entire days in UTC
      const formattedStartDate = moment(startDate).startOf('day').toISOString();
      const formattedEndDate = moment(endDate).endOf('day').toISOString();

      const [locationsRes, productsRes, productsVRes, billsRes, billsValisesRes, rapportsRes] = await Promise.all([
        axios.get("/api/Locations/getAllLocations", { headers }),
        axios.get("/api/Product/getProduct?slim=true", { headers }),
        axios.get("/api/ProductV/getProductV?slim=true", { headers }),
        axios.get(`/api/Bills/getAllBills?slim=true&startDate=${formattedStartDate}&endDate=${formattedEndDate}`, { headers }),
        axios.get(`/api/BillsValises/getAllBillsV?slim=true&startDate=${formattedStartDate}&endDate=${formattedEndDate}`, { headers }),
        axios.get(`/api/RapportsSpintoss/GetRapportsSpintoss?slim=true&startDate=${formattedStartDate}&endDate=${formattedEndDate}`, { headers })
      ]);

      setLocations(locationsRes.data || []);
      setProducts(productsRes.data || []);
      setProductsV(productsVRes.data || []);
      setBills(billsRes.data || []);
      setBillsValises(billsValisesRes.data || []);
      setRapports(rapportsRes.data?.RapportsSpintoss || rapportsRes.data?.rapportsSpintoss || []);
    } catch (err) {
      console.error("Error fetching Spintoss stats data:", err);
      setError("Une erreur est survenue lors du chargement des données. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 1. Identify Spintoss Locations (containing "spintoss")
  const spinLocations = useMemo(() => {
    return locations.filter(loc => {
      const nameLower = (loc.name || "").toLowerCase();
      return nameLower.includes("spintoss");
    });
  }, [locations]);

  // Cache locations mapping
  const locationsMap = useMemo(() => {
    const map = new Map();
    locations.forEach(loc => {
      const id = extractLocId(loc._id);
      if (id) map.set(id, (loc.name || "").toLowerCase());
    });
    return map;
  }, [locations]);

  // 2. Pre-calculate Product categories & details in maps
  const productPinMap = useMemo(() => {
    const map = new Map();
    products.forEach(p => {
      map.set(`prod_${extractLocId(p._id)}`, { ...p, isSub: false });
      if (p.subProducts && Array.isArray(p.subProducts)) {
        p.subProducts.forEach(sub => {
          map.set(`sub_${extractLocId(sub._id)}`, {
            ...sub,
            parentName: p.name,
            parentId: p._id,
            parentCategory: p.category,
            isSub: true
          });
        });
      }
    });
    return map;
  }, [products]);

  const productValisesMap = useMemo(() => {
    const map = new Map();
    productsV.forEach(p => {
      map.set(`prod_${extractLocId(p._id)}`, { ...p, isSub: false });
      if (p.subProductsV && Array.isArray(p.subProductsV)) {
        p.subProductsV.forEach(sub => {
          map.set(`sub_${extractLocId(sub._id)}`, {
            ...sub,
            parentName: p.name,
            parentId: p._id,
            parentCategory: p.category,
            isSub: true
          });
        });
      }
    });
    return map;
  }, [productsV]);

  // 3. Resolve location name dynamically
  const resolveLoc = useCallback((raw) => {
    if (!raw) return "";
    let idStr = "";
    if (typeof raw === "string") {
      idStr = raw;
    } else if (raw.$oid) {
      idStr = raw.$oid;
    } else if (raw._id) {
      idStr = typeof raw._id === "string" ? raw._id : (raw._id.$oid || raw._id.toString());
    } else if (raw.name) {
      idStr = raw.name;
    } else {
      idStr = raw.toString();
    }
    let name = idStr.toLowerCase();
    if (/^[0-9a-fA-F]{24}$/.test(name)) {
      name = locationsMap.get(name) || "";
    }
    return name;
  }, [locationsMap]);

  // 4. Classify Entity (exact replica of Rbe.js logic)
  const classifyEntity = useCallback((locName, bill) => {
    const name = (locName || "").toString().toLowerCase();
    const n = name.replace(/\s+/g, '');

    // BagnGo / Bng
    if (n.includes('bagngo') || n.includes('bng')) return 'BagnGo';

    // E-space
    if (n.includes('espace') || n.includes('e-space')) return 'E-space';

    if (bill) {
      const userName = (bill.user?.name || bill.userName || "").toString().toLowerCase();
      const userRole = (bill.user?.role || "").toString().toLowerCase();
      if (userName.includes('espace') || userName.includes('e-space') || userRole === "userespace") {
        return "E-space";
      }
      if (userName.includes('bagngo') || userName.includes('bng') || userRole === "userbagngo") {
        return "BagnGo";
      }
      if (userName.includes('spintoss') || userName.includes('spin') || userRole === "userspintoss") {
        return "Spintoss";
      }
    }

    // Spintoss
    if (n.includes('spintoss') || n.includes('spin')) return 'Spintoss';

    // Twin Toys
    const ttLocs = [
      "tunisia mall", "manar city", "jardins d'el menzah",
      "tp-tunisia mallmarge", "tp-manar citymarge", "tp-jardins d'el menzahmarge",
      "tp-tunisiamall", "tp-manarcity", "tp-lesjardinsdelmenzah"
    ];
    const ttLocsNS = ttLocs.map(l => l.replace(/\s+/g, ''));
    if (ttLocsNS.some(l => n.includes(l))) return 'Twin Toys';

    // Twin Park Locations split
    if (n.includes('manar')) return 'Manar City';
    if (n.includes('tunisia')) return 'Tunisia Mall';
    if (n.includes('jardin') || n.includes('menzah')) return "Jardins d'El Menzah";
    if (n.includes('twin')) return 'Manar City'; // fallback

    // Administration
    if (n.includes('administration') || n.includes('admin')) return 'Administration';

    // Wellness / All others
    return 'Wellness';
  }, []);

  // 5. Resolve Rapport location ID
  const resolveRapportLocationId = useCallback((entryLocalisation) => {
    if (!entryLocalisation) return null;
    const nameLower = entryLocalisation.toLowerCase().replace(/\s+/g, '');
    const foundLoc = locations.find(loc => {
      const locNameLower = (loc.name || "").toLowerCase().replace(/\s+/g, '');
      return locNameLower === nameLower || locNameLower.includes(nameLower) || nameLower.includes(locNameLower);
    });
    return foundLoc ? extractLocId(foundLoc._id) : null;
  }, [locations]);

  // Filter Spintoss Rapports
  const filteredRapports = useMemo(() => {
    return rapports.filter(entry => {
      const entryLocId = resolveRapportLocationId(entry.localisation);
      if (!entryLocId) return false;

      // Filter by selected location
      if (localisation !== "All") {
        return entryLocId === localisation;
      }
      return true;
    });
  }, [rapports, resolveRapportLocationId, localisation]);

  // 6. Aggregate Sales Metrics
  const processedMetrics = useMemo(() => {
    let posSales = 0;
    let reportsCA = 0;
    let totalItemsSold = 0;
    let spintossBillsCount = 0;
    const dailySalesMap = {};
    const categoryMap = {
      FOOD: { name: "FOOD (Plats & Snacking)", quantity: 0, sales: 0 },
      BEVERAGES: { name: "BEVERAGES (Boissons)", quantity: 0, sales: 0 },
      DEALS: { name: "DEALS (Formules & Offres)", quantity: 0, sales: 0 },
      Autre: { name: "Autre / Divers", quantity: 0, sales: 0 }
    };

    const productSalesMap = {};

    // Sum manual reports CA
    filteredRapports.forEach(entry => {
      reportsCA += (entry.TotalAmount || 0);
    });

    // A. Process POS Bills (standard PIN) with discount applied
    bills.forEach(bill => {
      if (bill.isStaffAddition) return;
      if (bill.status === "cancelled") return;

      const billDate = new Date(bill.date || bill.createdAt);
      const dateKey = moment(billDate).format("YYYY-MM-DD");
      let billSalesAmount = 0;
      let billHasSpintossItems = false;

      const discountFactor = 1 - (bill.discount || 0) / 100;

      bill.items?.forEach(item => {
        if (item.isCancelled) return;

        // Resolve item details
        const itemId = item.id || item._id;
        const itemIdStr = itemId ? extractLocId(itemId) : "";
        const prodMatch = productPinMap.get(`prod_${itemIdStr}`);
        const subMatch = productPinMap.get(`sub_${itemIdStr}`);

        // Resolve location name & ID matching Rbe.js
        let locId = bill.localisation ? extractLocId(bill.localisation) : null;
        const itemLocId = subMatch ? extractLocId(subMatch.localisation) : (prodMatch ? extractLocId(prodMatch.localisation) : null);
        const resolvedLocId = locId || itemLocId;
        const name = resolvedLocId ? (locationsMap.get(resolvedLocId) || "") : "";

        const entity = classifyEntity(name, bill);
        if (entity !== 'Spintoss') return;

        // Apply boutique / localisation filter if not "All"
        if (localisation !== "All" && resolvedLocId !== localisation) return;

        billHasSpintossItems = true;

        const quantity = item.quantity || 1;
        const sellingPrice = item.price || 0;
        const salesAmount = sellingPrice * quantity * discountFactor;

        let category = "Autre";
        let pName = item.name || "Produit inconnu";

        if (item.isDeal) {
          category = "DEALS";
          pName = prodMatch ? prodMatch.name : item.name;
        } else if (subMatch && subMatch.isSub) {
          category = subMatch.parentCategory || "FOOD";
          pName = `${subMatch.parentName} - ${subMatch.name}`;
        } else if (prodMatch && !prodMatch.isSub) {
          category = prodMatch.category || "FOOD";
          pName = prodMatch.name;
        }

        const catKey = (category === "FOOD" || category === "BEVERAGES" || category === "DEALS") ? category : "Autre";

        posSales += salesAmount;
        totalItemsSold += quantity;
        billSalesAmount += salesAmount;

        categoryMap[catKey].quantity += quantity;
        categoryMap[catKey].sales += salesAmount;

        if (itemIdStr) {
          if (!productSalesMap[itemIdStr]) {
            productSalesMap[itemIdStr] = { name: pName, category: catKey, quantity: 0, sales: 0 };
          }
          productSalesMap[itemIdStr].quantity += quantity;
          productSalesMap[itemIdStr].sales += salesAmount;
        }
      });

      if (billHasSpintossItems) {
        dailySalesMap[dateKey] = (dailySalesMap[dateKey] || 0) + billSalesAmount;
        spintossBillsCount++;
      }
    });

    // B. Process POS Valises Bills with discount applied
    billsValises.forEach(bill => {
      if (bill.status === "cancelled") return;

      const billDate = new Date(bill.date || bill.createdAt);
      const dateKey = moment(billDate).format("YYYY-MM-DD");
      let billSalesAmount = 0;
      let billHasSpintossItems = false;

      const discountFactor = 1 - (bill.discount || 0) / 100;

      bill.items?.forEach(item => {
        if (item.isCancelled) return;

        // Resolve item details
        const itemId = item.id || item._id;
        const itemIdStr = itemId ? extractLocId(itemId) : "";
        const prodMatch = productValisesMap.get(`prod_${itemIdStr}`);
        const subMatch = productValisesMap.get(`sub_${itemIdStr}`);

        // Resolve location name & ID matching Rbe.js
        let locId = bill.localisation ? extractLocId(bill.localisation) : null;
        const itemLocId = subMatch ? extractLocId(subMatch.localisation) : (prodMatch ? extractLocId(prodMatch.localisation) : null);
        const resolvedLocId = locId || itemLocId;
        const name = resolvedLocId ? (locationsMap.get(resolvedLocId) || "") : "";

        const entity = classifyEntity(name, bill);
        if (entity !== 'Spintoss') return;

        // Apply boutique / localisation filter if not "All"
        if (localisation !== "All" && resolvedLocId !== localisation) return;

        billHasSpintossItems = true;

        const quantity = item.quantity || 1;
        const sellingPrice = item.price || 0;
        const salesAmount = sellingPrice * quantity * discountFactor;

        let category = "Autre";
        let pName = item.name || "Produit inconnu";

        if (item.isDeal) {
          category = "DEALS";
          pName = prodMatch ? prodMatch.name : item.name;
        } else if (subMatch && subMatch.isSub) {
          category = subMatch.parentCategory || "FOOD";
          pName = `${subMatch.parentName} - ${subMatch.name}`;
        } else if (prodMatch && !prodMatch.isSub) {
          category = prodMatch.category || "FOOD";
          pName = prodMatch.name;
        }

        const catKey = (category === "FOOD" || category === "BEVERAGES" || category === "DEALS") ? category : "Autre";

        posSales += salesAmount;
        totalItemsSold += quantity;
        billSalesAmount += salesAmount;

        categoryMap[catKey].quantity += quantity;
        categoryMap[catKey].sales += salesAmount;

        if (itemIdStr) {
          if (!productSalesMap[itemIdStr]) {
            productSalesMap[itemIdStr] = { name: pName, category: catKey, quantity: 0, sales: 0 };
          }
          productSalesMap[itemIdStr].quantity += quantity;
          productSalesMap[itemIdStr].sales += salesAmount;
        }
      });

      if (billHasSpintossItems) {
        dailySalesMap[dateKey] = (dailySalesMap[dateKey] || 0) + billSalesAmount;
        spintossBillsCount++;
      }
    });

    // CA Retenu = max(CA POS, CA Rapports) matching Rbe.js
    const totalCA = Math.max(posSales, reportsCA);

    // Determine Best Sales Day
    let bestDayDate = "N/A";
    let bestDayAmount = 0;
    Object.entries(dailySalesMap).forEach(([date, amt]) => {
      if (amt > bestDayAmount) {
        bestDayAmount = amt;
        bestDayDate = date;
      }
    });

    // Split rankings
    const allProductsRanked = Object.values(productSalesMap).sort((a, b) => b.quantity - a.quantity);
    const foodRankings = allProductsRanked.filter(p => p.category === "FOOD").slice(0, 20);
    const beveragesRankings = allProductsRanked.filter(p => p.category === "BEVERAGES").slice(0, 20);
    const dealsRankings = allProductsRanked.filter(p => p.category === "DEALS").slice(0, 20);

    const topProductGlobal = allProductsRanked.find(p => p.category !== "DEALS") || null;
    const topFoodProduct = foodRankings[0] || null;
    const topBeverageProduct = beveragesRankings[0] || null;
    const topDealProduct = dealsRankings[0] || null;

    return {
      totalCA,
      posSales,
      reportsCA,
      totalItemsSold,
      spintossBillsCount,
      averageBasket: spintossBillsCount > 0 ? posSales / spintossBillsCount : 0,
      dailySalesMap,
      categoryMap,
      bestDay: { date: bestDayDate, amount: bestDayAmount },
      foodRankings,
      beveragesRankings,
      dealsRankings,
      topProductGlobal,
      topFoodProduct,
      topBeverageProduct,
      topDealProduct
    };
  }, [bills, billsValises, filteredRapports, productPinMap, productValisesMap, locationsMap, classifyEntity, resolveLoc, extractLocId, localisation]);

  // Daily Trend Chart Data
  const dailyChartData = useMemo(() => {
    const list = [];
    const start = moment(startDate);
    const end = moment(endDate);

    for (let m = moment(start); m.isSameOrBefore(end); m.add(1, 'days')) {
      const dateStr = m.format("YYYY-MM-DD");
      list.push({
        dateLabel: m.format("DD/MM"),
        sales: processedMetrics.dailySalesMap[dateStr] || 0
      });
    }
    return list;
  }, [startDate, endDate, processedMetrics.dailySalesMap]);

  // Categories Distribution (PieChart)
  const categoriesPieChartData = useMemo(() => {
    const data = [];
    let idCounter = 0;
    const colors = ['#10b981', '#059669', '#34d399', '#6ee7b7'];

    Object.entries(processedMetrics.categoryMap).forEach(([cat, stats]) => {
      if (stats.sales > 0) {
        const pct = processedMetrics.totalCA > 0 ? ((stats.sales / processedMetrics.totalCA) * 100).toFixed(1) + "%" : "0%";
        data.push({
          id: idCounter++,
          value: stats.sales,
          label: `${cat} (${pct})`,
          color: colors[idCounter % colors.length]
        });
      }
    });

    return data;
  }, [processedMetrics.categoryMap, processedMetrics.totalCA]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(value || 0);
  };

  return (
    <div className="spintoss-dashboard-container">
      <div className="spintoss-header">
        <h1>Statistiques Spintoss</h1>
        <p>Suivi analytique des ventes de restauration, boissons et offres spéciales (Deals)</p>
      </div>

      {/* Filtres */}
      <div className="spintoss-filters">
        <div className="spintoss-filter-group">
          <label>Boutique / Localisation</label>
          <select value={localisation} onChange={(e) => setLocalisation(e.target.value)}>
            <option value="All">Toutes les boutiques Spintoss</option>
            {spinLocations.map(loc => (
              <option key={loc._id} value={extractLocId(loc._id)}>{loc.name}</option>
            ))}
          </select>
        </div>
        <div className="spintoss-filter-group">
          <label>Date de début</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="spintoss-filter-group">
          <label>Date de fin</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
      </div>

      {loading && <p style={{ textAlign: 'center', color: '#059669', fontWeight: 'bold' }}>Chargement des données Spintoss en cours...</p>}
      {error && <p style={{ textAlign: 'center', color: '#dc2626', fontWeight: 'bold' }}>{error}</p>}

      {/* KPIs Section */}
      <div className="spintoss-kpis">
        <div className="spintoss-kpi-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div className="spintoss-kpi-title">Chiffre d'Affaires</div>
          <div className="spintoss-kpi-value primary">{formatCurrency(processedMetrics.totalCA)}</div>
          <small style={{ fontWeight: 'bold', color: '#047857', marginTop: '4px' }}>
            Retenu (Max de POS & Rapports)
          </small>
          <small style={{ color: '#4b5563', fontSize: '0.78rem' }}>
            POS: {formatCurrency(processedMetrics.posSales)} | Rapports: {formatCurrency(processedMetrics.reportsCA)}
          </small>
        </div>
        <div className="spintoss-kpi-card">
          <div className="spintoss-kpi-title">Meilleur Jour de Vente</div>
          <div className="spintoss-kpi-value warning" style={{ fontSize: '1.6rem', padding: '6px 0' }}>
            {processedMetrics.bestDay.date !== "N/A" ? moment(processedMetrics.bestDay.date).format("DD MMM YYYY") : "N/A"}
          </div>
          <small style={{ fontWeight: 'bold', color: '#b45309' }}>
            Volume: {formatCurrency(processedMetrics.bestDay.amount)}
          </small>
        </div>
        <div className="spintoss-kpi-card">
          <div className="spintoss-kpi-title">Panier Moyen</div>
          <div className="spintoss-kpi-value info">{formatCurrency(processedMetrics.averageBasket)}</div>
          <small>Tickets enregistrés: {processedMetrics.spintossBillsCount}</small>
        </div>
        <div className="spintoss-kpi-card">
          <div className="spintoss-kpi-title">Meilleur Produit FOOD</div>
          <div className="spintoss-kpi-value primary" style={{ fontSize: '1.2rem', padding: '10px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={processedMetrics.topFoodProduct?.name || "Aucun"}>
            {processedMetrics.topFoodProduct ? processedMetrics.topFoodProduct.name : "Aucun"}
          </div>
          <small>Vendu: {processedMetrics.topFoodProduct?.quantity || 0} fois</small>
        </div>
      </div>

      {/* Graphiques Section */}
      <div className="spintoss-charts">
        {/* Évolution Journalière */}
        <div className="spintoss-chart-container" style={{ flex: 2 }}>
          <div className="spintoss-chart-title">📈 Évolution du Chiffre d'Affaires Quotidien (POS Net)</div>
          {dailyChartData.length > 0 ? (
            <LineChart
              dataset={dailyChartData}
              xAxis={[{ scaleType: 'point', dataKey: 'dateLabel' }]}
              series={[{ dataKey: 'sales', color: '#10b981', label: 'Ventes (TND)' }]}
              width={700}
              height={260}
            />
          ) : (
            <p style={{ margin: 'auto', color: '#64748b' }}>Aucune donnée de vente pour cette période.</p>
          )}
        </div>

        {/* Répartition Catégories */}
        <div className="spintoss-chart-container">
          <div className="spintoss-chart-title">🍕 Ventes par Secteur Spintoss</div>
          {categoriesPieChartData.length > 0 ? (
            <PieChart
              series={[{
                data: categoriesPieChartData,
                innerRadius: 30,
                outerRadius: 80,
                paddingAngle: 5,
                cornerRadius: 6,
              }]}
              width={380}
              height={260}
              slotProps={{ legend: { position: { vertical: 'bottom', horizontal: 'middle' }, labelStyle: { fill: '#374151', fontSize: 12 } } }}
            />
          ) : (
            <p style={{ margin: 'auto', color: '#64748b' }}>Aucune vente enregistrée.</p>
          )}
        </div>
      </div>

      {/* Synthèse par Catégorie */}
      <div className="spintoss-table-container">
        <h3>📦 Synthèse de l'activité Spintoss par type</h3>
        <div className="table-responsive">
          <table className="spintoss-table">
            <thead>
              <tr>
                <th>Type de Produit (Catégorie)</th>
                <th>Volume Vendu</th>
                <th>CA Réel (Ventes)</th>
                <th>Part du Chiffre d'Affaires</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(processedMetrics.categoryMap).map(([cat, stats]) => {
                const pct = processedMetrics.totalCA > 0 ? ((stats.sales / processedMetrics.totalCA) * 100).toFixed(1) + "%" : "0%";
                return (
                  <tr key={cat}>
                    <td style={{ fontWeight: 'bold', color: '#111827' }}>{stats.name}</td>
                    <td style={{ fontWeight: '700', color: '#374151' }}>{stats.quantity || 0} pcs</td>
                    <td style={{ fontWeight: '700', color: '#111827' }}>{formatCurrency(stats.sales)}</td>
                    <td style={{ fontWeight: 'bold', color: '#047857' }}>{pct}</td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: '2px solid rgba(16, 185, 129, 0.3)', fontWeight: 'bold' }}>
                <td style={{ color: '#111827' }}>TOTAL SPINTOSS CUMULÉ</td>
                <td style={{ color: '#111827', fontWeight: '800' }}>{processedMetrics.totalItemsSold} pcs</td>
                <td style={{ color: '#111827', fontWeight: '800' }}>{formatCurrency(processedMetrics.totalCA)}</td>
                <td style={{ color: '#047857', fontWeight: '800' }}>100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Grid pour les classements */}
      <div className="spintoss-category-rankings-section">
        <h3 className="section-title">🏆 Classement détaillé des Ventes Spintoss</h3>
        <div className="spintoss-category-rankings-grid">

          {/* Classement FOOD */}
          <div className="spintoss-table-container ranking-card">
            <h4 className="spintoss-ranking-card-title">
              <span style={{ marginRight: '8px', fontSize: '1.4rem' }}>🍔</span>
              Meilleures Ventes FOOD
            </h4>
            <div className="table-responsive spintoss-ranking-table-wrapper">
              <table className="spintoss-table compact">
                <thead>
                  <tr>
                    <th>Rang</th>
                    <th>Produit</th>
                    <th style={{ textAlign: 'center' }}>Qté Vendue</th>
                    <th>CA Réel</th>
                  </tr>
                </thead>
                <tbody>
                  {processedMetrics.foodRankings.map((p, index) => (
                    <tr key={index}>
                      <td style={{ fontWeight: 'bold', color: '#10b981' }}>#{index + 1}</td>
                      <td style={{ fontWeight: '600', color: '#111827' }}>{p.name}</td>
                      <td style={{ textAlign: 'center', fontWeight: '700', color: '#374151' }}>{p.quantity}</td>
                      <td style={{ fontWeight: '700', color: '#111827' }}>{formatCurrency(p.sales)}</td>
                    </tr>
                  ))}
                  {processedMetrics.foodRankings.length === 0 && (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', color: '#64748b', padding: '40px 10px', fontStyle: 'italic' }}>
                        Aucun plat vendu sur cette période.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Classement BEVERAGES */}
          <div className="spintoss-table-container ranking-card">
            <h4 className="spintoss-ranking-card-title">
              <span style={{ marginRight: '8px', fontSize: '1.4rem' }}>🥤</span>
              Meilleures Ventes BEVERAGES
            </h4>
            <div className="table-responsive spintoss-ranking-table-wrapper">
              <table className="spintoss-table compact">
                <thead>
                  <tr>
                    <th>Rang</th>
                    <th>Boisson</th>
                    <th style={{ textAlign: 'center' }}>Qté Vendue</th>
                    <th>CA Réel</th>
                  </tr>
                </thead>
                <tbody>
                  {processedMetrics.beveragesRankings.map((p, index) => (
                    <tr key={index}>
                      <td style={{ fontWeight: 'bold', color: '#059669' }}>#{index + 1}</td>
                      <td style={{ fontWeight: '600', color: '#111827' }}>{p.name}</td>
                      <td style={{ textAlign: 'center', fontWeight: '700', color: '#374151' }}>{p.quantity}</td>
                      <td style={{ fontWeight: '700', color: '#111827' }}>{formatCurrency(p.sales)}</td>
                    </tr>
                  ))}
                  {processedMetrics.beveragesRankings.length === 0 && (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', color: '#64748b', padding: '40px 10px', fontStyle: 'italic' }}>
                        Aucune boisson vendue sur cette période.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>

        <div className="spintoss-category-rankings-grid" style={{ marginTop: '25px', gridTemplateColumns: '1fr' }}>
          {/* Classement DEALS */}
          <div className="spintoss-table-container ranking-card">
            <h4 className="spintoss-ranking-card-title">
              <span style={{ marginRight: '8px', fontSize: '1.4rem' }}>🏷️</span>
              Meilleurs DEALS & Formules
            </h4>
            <div className="table-responsive spintoss-ranking-table-wrapper">
              <table className="spintoss-table compact">
                <thead>
                  <tr>
                    <th>Rang</th>
                    <th>Formule / Deal</th>
                    <th style={{ textAlign: 'center' }}>Qté Vendue</th>
                    <th>CA Réel</th>
                  </tr>
                </thead>
                <tbody>
                  {processedMetrics.dealsRankings.map((p, index) => (
                    <tr key={index}>
                      <td style={{ fontWeight: 'bold', color: '#047857' }}>#{index + 1}</td>
                      <td style={{ fontWeight: '600', color: '#111827' }}>{p.name}</td>
                      <td style={{ textAlign: 'center', fontWeight: '700', color: '#374151' }}>{p.quantity}</td>
                      <td style={{ fontWeight: '700', color: '#111827' }}>{formatCurrency(p.sales)}</td>
                    </tr>
                  ))}
                  {processedMetrics.dealsRankings.length === 0 && (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', color: '#64748b', padding: '40px 10px', fontStyle: 'italic' }}>
                        Aucune formule vendue sur cette période.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};

export default SpintossStats;
